'use client';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {
  Fragment,
  HTMLProps,
  PropsWithChildren,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  use,
} from 'react';

import { Styles, css } from '@flipster/styled-system/css';

import { useControllableState } from '../../misc/state/use-controllable-state';

interface DropdownContextType {
  open: boolean;
  setOpen: (bool: boolean | undefined) => void;
  refs: ReturnType<typeof useFloating>['refs'];
  floatingStyles: ReturnType<typeof useFloating>['floatingStyles'];
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
}

const DropdownContext = createContext<DropdownContextType | undefined>(undefined);

export const useDropdown = () => {
  const ctx = use(DropdownContext);
  if (!ctx) throw new Error('Dropdown components must be used within Dropdown.Root');
  return ctx;
};

export interface DropdownRootProps {
  placement?: 'top-start' | 'top' | 'top-end' | 'bottom-start' | 'bottom' | 'bottom-end';
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  inheritWidth?: boolean;
  offset?: number;
  disableDismiss?: boolean;
  dismissOnAncestorScroll?: boolean;
  onSizeApply?: (params: { availableHeight: number; availableWidth: number }) => void;
  children: ReactNode;
}

const Root = ({
  placement = 'bottom-start',
  open,
  onOpen,
  onClose,
  inheritWidth = true,
  offset: offsetValue = 6,
  disableDismiss = false,
  dismissOnAncestorScroll = false,
  onSizeApply,
  children,
}: DropdownRootProps) => {
  const [controlOpen, setControlOpen] = useControllableState<boolean>({
    defaultValue: false,
    value: open,
    onChange(value) {
      if (value) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
  });

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: controlOpen,
    onOpenChange: setControlOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({ padding: 4 }),
      shift(),
      size({
        apply({ elements, rects, availableHeight, availableWidth }) {
          if (inheritWidth) {
            const triggerWidth = rects.reference.width;
            Object.assign(elements.floating.style, {
              minWidth: `${triggerWidth}px`,
            });
          }
          onSizeApply?.({ availableHeight, availableWidth });
        },
      }),
    ],
  });

  const role = useRole(context);
  const dismiss = useDismiss(context, { enabled: !disableDismiss, ancestorScroll: dismissOnAncestorScroll });
  const click = useClick(context, { toggle: true });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <DropdownContext.Provider
      value={{
        open: Boolean(controlOpen),
        setOpen: setControlOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
      }}
    >
      {children}
    </DropdownContext.Provider>
  );
};

export interface TriggerProps extends PropsWithChildren {
  disabled?: boolean;
  css?: Styles;
  asChild?: boolean;
}
const Trigger = ({ disabled, css: cssProps, children, asChild }: TriggerProps) => {
  const { refs, getReferenceProps } = useDropdown();

  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      getReferenceProps({ disabled, ref: refs.setReference, ...(children.props as HTMLProps<HTMLElement>) }),
    );
  }

  return (
    <button
      type='button'
      className={css({ w: 'max-content' }, cssProps)}
      ref={refs.setReference}
      disabled={disabled}
      {...getReferenceProps()}
    >
      {children}
    </button>
  );
};

const Content = ({ portal, css: cssProps, children }: PropsWithChildren<{ portal?: boolean; css?: Styles }>) => {
  const { open, refs, floatingStyles, getFloatingProps } = useDropdown();

  const Container = portal ? FloatingPortal : Fragment;

  if (!open) return null;
  return (
    <Container {...(portal ? { id: 'modal' } : {})}>
      <div
        className={css(
          {
            // 임시 디자인 팀과 협의 나중에 background.default.level-1.5 같은걸로 시맨틱 컬러에 포함 예정
            bgColor: 'gray.50',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-2',
            rounded: 8,
          },
          portal && { zIndex: 'modal' },
          cssProps,
        )}
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </Container>
  );
};

Root.displayName = 'Dropdown.Root';
Trigger.displayName = 'Dropdown.Trigger';
Content.displayName = 'Dropdown.Content';

export { Root, Trigger, Content };
